import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import {
  Loader2,
  ShoppingBag,
  ArrowLeft,
  User,
  Calendar,
  Target,
  DollarSign,
  Clock,
  CheckCircle,
  Image,
  FileText,
  MessageCircle,
  Send,
  Trash2,
  Reply
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const CommunityCosplayPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [kitPricing, setKitPricing] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [qName, setQName] = useState('');
  const [qEmail, setQEmail] = useState('');
  const [qMsg, setQMsg] = useState('');
  const [qSubmitting, setQSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [cosplayRes, commentsRes] = await Promise.all([
        api.get(`/community/${id}`),
        api.get(`/community/${id}/comments`)
      ]);
      setData(cosplayRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error('Failed to load cosplay');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/community/${id}/comments`, {
        content: newComment.trim()
      });
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (commentId) => {
    if (!replyContent.trim() || !user) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/community/${id}/comments/${commentId}/reply`, {
        content: replyContent.trim()
      });
      setComments([...comments, response.data]);
      setReplyContent('');
      setReplyingTo(null);
      toast.success('Reply added!');
    } catch (error) {
      toast.error('Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/community/${id}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId && c.parent_id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  useEffect(() => { api.get('/kit-pricing').then((r) => setKitPricing(r.data)).catch(() => {}); }, []);
  const openQuote = () => { setQName(user?.display_name || ''); setQEmail(user?.email || ''); setQuoteOpen(true); };
  const submitQuote = async () => {
    if (!qName.trim() || !qEmail.trim()) { toast.error('Name and email are required.'); return; }
    setQSubmitting(true);
    try {
      const res = await api.post('/quote-request', { tier: data?.cosplay?.mandomaster_tier || 'neither', complete: !!data?.cosplay?.mandomaster_kit_complete, client_name: qName, client_email: qEmail, message: qMsg, armor_list: data?.cosplay?.mandomaster_armor_list || '' });
      setQuoteOpen(false); setQMsg('');
      toast.success(`Quote requested${res.data?.quote_number ? ` (${res.data.quote_number})` : ''} — The Star Forge will be in touch!`);
    } catch {
      toast.error('Could not submit your quote. Please try again.');
    } finally { setQSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-slate-700 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-700">Cosplay not found</p>
        <Link to="/social">
          <Button className="mt-4 win-btn-default">Back to Community</Button>
        </Link>
      </div>
    );
  }

  const { cosplay, owner, task_categories, photos, notes } = data;

  // Organize comments into threads
  const parentComments = comments.filter(c => !c.parent_id);
  const getReplies = (parentId) => comments.filter(c => c.parent_id === parentId);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="community-cosplay-page">
      {/* Back button */}
      <Link
        to="/social"
        className="inline-flex items-center gap-2 text-slate-700 hover:underline"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Community
      </Link>

      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Cover Image */}
          <div className="w-full md:w-64 aspect-square overflow-hidden win-sunken flex-shrink-0">
            {cosplay.cover_image ? (
              <img
                src={cosplay.cover_image}
                alt={cosplay.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-16 h-16 text-slate-500" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-3xl font-heading font-bold text-black">{cosplay.title}</h1>
            <p className="text-xl text-black mt-1">{cosplay.character_name}</p>
            {cosplay.series_name && (
              <p className="text-slate-700">{cosplay.series_name}</p>
            )}

            {/* Owner */}
            <div className="flex items-center gap-3 mt-4 p-3 win-well">
              {owner.profile_picture ? (
                <img
                  src={owner.profile_picture}
                  alt={owner.display_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[var(--win-titlebar-start)] flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <p className="text-black font-medium">{owner.display_name}</p>
                <p className="text-xs text-slate-700">Cosplay Creator</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div className="text-center p-2 win-sunken">
                <p className="text-lg font-bold text-black">{cosplay.progress}%</p>
                <p className="text-xs text-slate-700">Progress</p>
              </div>
              <div className="text-center p-2 win-sunken">
                <p className="text-lg font-bold text-black">${cosplay.total_spent?.toFixed(0) || 0}</p>
                <p className="text-xs text-slate-700">Spent</p>
              </div>
              <div className="text-center p-2 win-sunken">
                <p className="text-lg font-bold text-black">{cosplay.total_time_hours?.toFixed(1) || 0}h</p>
                <p className="text-xs text-slate-700">Time</p>
              </div>
              <div className="text-center p-2 win-sunken">
                <p className="text-lg font-bold text-black">{photos.length}</p>
                <p className="text-xs text-slate-700">Photos</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <Progress value={cosplay.progress} className="h-2" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['overview', 'photos', 'comments'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium ${activeTab === tab ? 'win-selected' : ''}`}
            data-testid={`tab-${tab}`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'photos' && `Photos (${photos.length})`}
            {tab === 'comments' && `Comments (${comments.length})`}
          </button>
        ))}
      </div>

      {cosplay.source === 'mandomaster' && (
        <div className="glass-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ShoppingBag className="w-5 h-5 text-black flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-black font-medium text-sm sm:text-base">Want this kit made for you?</p>
              <p className="text-slate-700 text-xs sm:text-sm">Request a quote from The Star Forge for the full armor set.</p>
              {cosplay.mandomaster_kit_complete && kitPricing?.tiers?.[cosplay.mandomaster_tier] != null && (
                <p className="text-black font-semibold text-sm mt-0.5">Estimated: ~${kitPricing.tiers[cosplay.mandomaster_tier]} <span className="text-slate-600 font-normal text-xs">(quote estimate)</span></p>
              )}
            </div>
          </div>
          <Button
            onClick={openQuote}
            className="win-btn-accent font-semibold flex-shrink-0"
          >
            <ShoppingBag className="w-4 h-4 mr-2" /> Request a Quote
          </Button>
        </div>
      )}

      {quoteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !qSubmitting && setQuoteOpen(false)}>
          <div
            className="win-window w-full max-w-md"
            role="dialog"
            aria-modal="true"
            aria-label="Request a Quote from The Star Forge"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="win-titlebar">
              <span>Request a Quote from The Star Forge</span>
              <button
                onClick={() => !qSubmitting && setQuoteOpen(false)}
                className="win-btn win-btn-icon"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-3">
              {cosplay.mandomaster_kit_complete && kitPricing?.tiers?.[cosplay.mandomaster_tier] != null && (
                <p className="text-sm text-slate-700">Estimated: <span className="text-black font-bold">~${kitPricing.tiers[cosplay.mandomaster_tier]}</span> — final price confirmed by The Star Forge.</p>
              )}
              <input placeholder="Your name" value={qName} onChange={(e) => setQName(e.target.value)} className="win-input w-full text-sm" />
              <input placeholder="Email" type="email" value={qEmail} onChange={(e) => setQEmail(e.target.value)} className="win-input w-full text-sm" />
              <Textarea placeholder="Anything else? (sizing, timeline, colors…)" rows={3} value={qMsg} onChange={(e) => setQMsg(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button onClick={submitQuote} disabled={qSubmitting} className="win-btn-default font-semibold">
                  {qSubmitting ? 'Sending…' : 'Send Quote Request'}
                </Button>
                <Button variant="ghost" onClick={() => setQuoteOpen(false)} disabled={qSubmitting}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Description */}
          {cosplay.description && (
            <div className="glass-card p-6 lg:col-span-2">
              <h3 className="text-lg font-heading font-semibold text-black mb-3">Description</h3>
              <p className="text-slate-800 whitespace-pre-wrap">{cosplay.description}</p>
            </div>
          )}

          {/* Task Categories */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-heading font-semibold text-black mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-black" />
              Tasks
            </h3>
            {task_categories.length === 0 ? (
              <p className="text-slate-700 text-sm">No tasks yet</p>
            ) : (
              <div className="space-y-4">
                {task_categories.map((category) => (
                  <div key={category.id}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-black">{category.name}</span>
                      <span className="text-sm text-slate-700">
                        {category.completed_tasks}/{category.total_tasks}
                      </span>
                    </div>
                    <Progress
                      value={category.total_tasks > 0 ? (category.completed_tasks / category.total_tasks) * 100 : 0}
                      className="h-1.5"
                    />
                    {category.tasks && category.tasks.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {category.tasks.map((task) => (
                          <div
                            key={task.id}
                            className={`text-sm flex items-center gap-2 ${task.completed ? 'text-black' : 'text-slate-700'}`}
                          >
                            <CheckCircle className={`w-4 h-4 ${task.completed ? 'text-black' : 'text-slate-500'}`} />
                            <span className={task.completed ? 'line-through' : ''}>{task.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-heading font-semibold text-black mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-black" />
              Notes
            </h3>
            {notes.length === 0 ? (
              <p className="text-slate-700 text-sm">No notes shared</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <div key={note.id} className="p-3 win-well">
                    <span className="text-xs text-slate-700 uppercase font-bold">{note.category}</span>
                    <p className="text-slate-800 text-sm mt-1">{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'photos' && (
        <div className="glass-card p-6">
          {photos.length === 0 ? (
            <div className="text-center py-8">
              <Image className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-700">No photos yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((photo) => (
                <div key={photo.id} className="aspect-square overflow-hidden win-sunken">
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Progress photo'}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'comments' && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-heading font-semibold text-black mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-black" />
            Comments &amp; Questions
          </h3>

          {/* New Comment Form */}
          {user ? (
            <form onSubmit={handleSubmitComment} className="mb-6">
              <Textarea
                placeholder="Ask a question or leave a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="mb-3"
                rows={3}
                data-testid="new-comment-input"
              />
              <Button
                type="submit"
                disabled={submitting || !newComment.trim()}
                className="win-btn-default gap-2"
                data-testid="submit-comment-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Post Comment
              </Button>
            </form>
          ) : (
            <div className="mb-6 p-4 win-well text-center">
              <p className="text-slate-700">
                <Link to="/login" className="font-bold underline">Sign in</Link> to leave a comment
              </p>
            </div>
          )}

          {/* Comments List */}
          {parentComments.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-700">No comments yet. Be the first to ask a question!</p>
            </div>
          ) : (
            <div>
              {parentComments.map((comment, idx) => (
                <div key={comment.id} data-testid={`comment-${comment.id}`}>
                  {idx > 0 && <div className="win-divider my-4" />}
                  {/* Main Comment */}
                  <div className="flex items-start gap-3">
                    {comment.user_profile_picture ? (
                      <img
                        src={comment.user_profile_picture}
                        alt={comment.user_name}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[var(--win-titlebar-start)] flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-white font-bold">
                          {comment.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-black">{comment.user_name}</span>
                        {comment.user_id === owner.id && (
                          <span className="text-xs font-bold">★ Creator</span>
                        )}
                        <span className="text-xs text-slate-600">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-slate-800 mt-1">{comment.content}</p>

                      {/* Actions */}
                      <div className="flex items-center gap-3 mt-2">
                        {user && (
                          <button
                            onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                            className="text-xs text-slate-700 hover:underline flex items-center gap-1"
                          >
                            <Reply className="w-3 h-3" />
                            Reply
                          </button>
                        )}
                        {user && (user.id === comment.user_id || user.id === owner.id) && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-xs text-slate-700 hover:text-[#8b0000] flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        )}
                      </div>

                      {/* Reply Form */}
                      {replyingTo === comment.id && (
                        <div className="mt-3">
                          <Textarea
                            placeholder="Write a reply..."
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            className="mb-2"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleReply(comment.id)}
                              disabled={submitting || !replyContent.trim()}
                              className="win-btn-default"
                            >
                              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reply'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setReplyingTo(null); setReplyContent(''); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Replies */}
                      {getReplies(comment.id).map((reply) => (
                        <div key={reply.id} className="mt-3 pl-3 border-l-2 border-[var(--win-shadow)]">
                          <div className="flex items-start gap-3">
                            {reply.user_profile_picture ? (
                              <img
                                src={reply.user_profile_picture}
                                alt={reply.user_name}
                                className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-[var(--win-titlebar-start)] flex items-center justify-center flex-shrink-0">
                                <span className="text-xs text-white font-bold">
                                  {reply.user_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-black text-sm">{reply.user_name}</span>
                                {reply.user_id === owner.id && (
                                  <span className="text-xs font-bold">★ Creator</span>
                                )}
                                <span className="text-xs text-slate-600">{formatDate(reply.created_at)}</span>
                              </div>
                              <p className="text-slate-800 text-sm mt-1">{reply.content}</p>

                              {user && (user.id === reply.user_id || user.id === owner.id) && (
                                <button
                                  onClick={() => handleDeleteComment(reply.id)}
                                  className="text-xs text-slate-700 hover:text-[#8b0000] flex items-center gap-1 mt-2"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunityCosplayPage;
